import React from 'react';
import styled from 'styled-components';

interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
  gradingMode?: 'normal' | 'q26';
}

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 10000;
  justify-content: center;
  align-items: center;
  padding: 20px;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    padding: 24px;
    max-height: 90vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e2e8f0;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ModalClose = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
`;

const ShortcutCategory = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
`;

const ShortcutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ShortcutItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;

  &:hover {
    background: #f0f9ff;
    border-color: #c7d2fe;
    transform: translateY(-1px);
  }
`;

const ShortcutDescription = styled.span`
  font-size: 14px;
  color: #475569;
  font-weight: 500;
`;

const KeyCombo = styled.kbd`
  display: flex;
  align-items: center;
  gap: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;
`;

const Key = styled.span`
  background: white;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  padding: 4px 8px;
  box-shadow: 0 2px 0 #94a3b8;
  color: #334155;
  white-space: nowrap;

  &.modifier {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #475569;
    font-size: 11px;
    padding: 3px 6px;
  }
`;

const Plus = styled.span`
  color: #94a3b8;
  font-size: 12px;
`;

const FooterNote = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 2px solid #f1f5f9;
  text-align: center;
  font-size: 13px;
  color: #64748b;
`;

// Helper function to format key combinations
const formatKeyCombo = (key: string): React.ReactNode => {
  const parts = key.split('+');
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Plus>+</Plus>}
          <Key className={['Ctrl', 'Alt', 'Shift', 'Meta'].includes(part) ? 'modifier' : ''}>
            {part === 'ArrowLeft' ? '←' :
             part === 'ArrowRight' ? '→' :
             part === 'Escape' ? 'Esc' :
             part}
          </Key>
        </React.Fragment>
      ))}
    </>
  );
};

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
  gradingMode = 'normal'
}) => {
  // Define shortcuts based on grading mode
  const shortcuts = {
    navigasiya: [
      { key: 'ArrowLeft', description: 'Əvvəlki şagird' },
      { key: 'ArrowRight', description: 'Növbəti şagird' },
      { key: 'Escape', description: 'Modalı bağla' },
    ],
    qiymətləndirmə: gradingMode === 'normal' ? [
      { key: '0', description: '0 bal' },
      { key: '1', description: '1/3 bal' },
      { key: '2', description: '1/2 bal' },
      { key: '3', description: '2/3 bal' },
      { key: '4', description: '1 bal' },
      { key: 'Enter', description: 'Yadda saxla və növbəti' },
    ] : [
      { key: 'Enter', description: 'Yadda saxla və növbəti' },
    ],
    alətlər: [
      { key: '?', description: 'Qısa yollar siyahısını aç' },
      { key: 'Shift+Q', description: 'Sual şəklini göstər' },
      { key: 'Shift+N', description: 'Qeyd dialoquna fokusa al' },
    ]
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay $isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <span>⌨️</span>
            Klaviatura Qısa Yolları
          </ModalTitle>
          <ModalClose onClick={onClose}>×</ModalClose>
        </ModalHeader>

        {Object.entries(shortcuts).map(([category, items]) => (
          <ShortcutCategory key={category}>
            <CategoryTitle>{category}</CategoryTitle>
            <ShortcutGrid>
              {items.map((item) => (
                <ShortcutItem key={item.key}>
                  <ShortcutDescription>{item.description}</ShortcutDescription>
                  <KeyCombo>{formatKeyCombo(item.key)}</KeyCombo>
                </ShortcutItem>
              ))}
            </ShortcutGrid>
          </ShortcutCategory>
        ))}

        <FooterNote>
          💡 İpucu: Bu pəncərəni istənilən vaxt <strong>?</strong> düyməsi ilə aça bilərsiniz
        </FooterNote>
      </ModalContent>
    </ModalOverlay>
  );
};

export default KeyboardShortcutsModal;
